import api, { currentRestaurantId, isLocalId } from "./api";
import { getQueuedActions, dequeueAction, setIdMapping, getIdMapping, moveToFailed } from "./indexedDb";

// Sync requests must reach the server — never the offline mocks in api.js
const direct = { skipOffline: true };

const SEND = {
  CREATE_ORDER: (a) => api.post("/orders/select-table", a.data, direct),
  CREATE_PARCEL: () => api.post("/orders/parcel", {}, direct),
  UPDATE_ITEMS: (a, id) => api.patch(`/orders/${id}/items`, { items: a.items }, direct),
  CANCEL_ORDER: (a, id) => api.post(`/orders/${id}/cancel`, { reason: a.reason }, direct),
  CLOSE_ORDER: (a, id) => api.post(`/orders/${id}/close`, { payment: a.payment }, direct),
  BILL_FROM_ORDER: (a, id) => api.post(`/bill/from-order/${id}`, a.data, direct),
  KOT_PRINT: (a, id) => api.post(`/orders/${id}/kot-print`, { forceReprint: false }, direct),
  TRANSFER_TABLE: (a, id) => api.patch(`/orders/${id}/transfer-table`, a.data, direct),
  MERGE_ORDERS: (a, id, sourceId) => api.post(`/orders/${id}/merge`, { sourceOrderId: sourceId }, direct),
};

const resolveId = async (id) => {
  if (!isLocalId(id)) return id;
  return (await getIdMapping(id)) || id;
};

// What to do with an action after the server answered with an error
const classify = (action, err) => {
  const status = err.response?.status;
  const data = err.response?.data || {};
  const message = data.message || data.error || err.message || "Sync failed";

  // Network down, server error or rate limited — try again later, keep order
  if (!status || status >= 500 || status === 408 || status === 429) return { outcome: "retry", message };

  // Logged out or subscription lapsed — keep everything until that is fixed
  if (status === 401) return { outcome: "blocked", message };
  if (status === 403 && ["SUBSCRIPTION_REQUIRED", "SUBSCRIPTION_EXPIRED", "SESSION_REVOKED"].includes(data.code)) {
    return { outcome: "blocked", message };
  }

  // The server already has this change (e.g. the response was lost on a flaky network)
  if (/already/i.test(message) || (action.type === "KOT_PRINT" && /closed order/i.test(message))) {
    return { outcome: "done", message };
  }

  return { outcome: "failed", message };
};

let running = false;

const syncQueue = async () => {
  const summary = { synced: 0, failed: 0, failures: [], idMap: {}, stoppedBy: null };
  const rid = currentRestaurantId();
  if (!rid) return summary;

  const queue = await getQueuedActions();

  for (const action of queue) {
    // Actions saved under another restaurant's login wait until that restaurant logs in
    if (action.restaurantId && action.restaurantId !== rid) continue;

    const send = SEND[action.type];
    if (!send) {
      await moveToFailed(action, `Unknown action type ${action.type}`);
      summary.failed += 1;
      continue;
    }

    const orderId = await resolveId(action.orderId);
    const sourceId = await resolveId(action.data?.sourceOrderId);

    // The offline order this action belongs to never made it to the server
    if (isLocalId(orderId) || isLocalId(sourceId)) {
      const reason = "The order this change belongs to could not be synced";
      await moveToFailed(action, reason);
      summary.failed += 1;
      summary.failures.push({ action, message: reason });
      continue;
    }

    try {
      const res = await send(action, orderId, sourceId);

      if (action.type === "CREATE_ORDER" || action.type === "CREATE_PARCEL") {
        // Persist the mapping before dequeuing so later actions can always resolve it
        await setIdMapping(action.localId, res.data._id);
        summary.idMap[action.localId] = res.data._id;
      }

      await dequeueAction(action.id);
      summary.synced += 1;
    } catch (err) {
      const { outcome, message } = classify(action, err);
      console.warn(`[Sync Manager] ${action.type} → ${outcome}: ${message}`);

      if (outcome === "done") {
        await dequeueAction(action.id);
        summary.synced += 1;
      } else if (outcome === "failed") {
        await moveToFailed(action, message);
        summary.failed += 1;
        summary.failures.push({ action, message });
      } else {
        // retry / blocked: stop here so later actions never jump ahead of this one
        summary.stoppedBy = outcome;
        break;
      }
    }
  }

  return summary;
};

/**
 * Replay queued offline actions against the server, in order.
 * Returns null when a sync is already running (in this or another tab).
 */
export const runSync = async ({ onStart } = {}) => {
  if (!navigator.onLine || running || !localStorage.getItem("token")) return null;

  const queue = await getQueuedActions();
  if (queue.length === 0) return null;

  running = true;
  onStart?.();
  try {
    // Only one tab may replay the queue at a time, otherwise orders get created twice
    if (navigator.locks?.request) {
      return await navigator.locks.request("cloudrasoi-sync", { ifAvailable: true }, (lock) =>
        lock ? syncQueue() : null
      );
    }
    return await syncQueue();
  } finally {
    running = false;
  }
};
